import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import {
  Activity,
  Armchair,
  Binoculars,
  BookImage,
  BriefcaseBusiness,
  CalendarDays,
  Car,
  ChefHat,
  Dumbbell,
  Fish,
  HelpCircle,
  House,
  Image,
  Info,
  Landmark,
  MapPinHouse,
  MountainSnow,
  PartyPopper,
  PersonStanding,
  PlaneTakeoff,
  RollerCoaster,
  Sailboat,
  Ship,
  TrainFront,
  TreePalm,
  Trophy,
  University,
  User,
  UserRound,
  Utensils,
  Waves,
  Wine,
} from 'lucide-react';
import {
  HikingIcon,
  JeepIcon,
  LionIcon,
  MassageIcon,
  MuseumIcon,
  PersonWalkingIcon,
  TeamIcon,
} from './legacy-category-icons';

export const getCategoryIcon = (category: string | null): LucideIcon => {
  switch (category) {
    case 'Accommodation':
      return House;
    case 'Activity':
      return PersonStanding;
    case 'Attraction':
      return Image;
    case 'Destination':
      return MountainSnow;
    case 'Dining':
      return Utensils;
    case 'Information':
      return Info;
    case 'Personnel':
      return User;
    case 'Transportation':
      return PlaneTakeoff;
    case 'Venue':
      return Landmark;
    case 'Other':
      return BriefcaseBusiness;
    default:
      return HelpCircle;
  }
};

// Lucide and the ported legacy icons share this render contract, so a map
// glyph can be either without the call sites caring.
export type CategoryGlyphIcon = ComponentType<{
  size?: number | string;
  strokeWidth?: number | string;
  className?: string;
}>;

// A resolved map glyph: the component plus the stroke weight the legacy
// asset baked in (1 for most lucide glyphs, 1.25 for the ground-transport
// set; the ported legacy icons bake their own weight and ignore the prop).
export type MarkerGlyph = {
  Icon: CategoryGlyphIcon;
  strokeWidth: number;
};

const glyph = (Icon: CategoryGlyphIcon, strokeWidth = 1): MarkerGlyph => ({
  Icon,
  strokeWidth,
});

// Legacy map resolution, ported 1:1 from the v2 map's `travelFromIcons`
// lookup + per-subcategory `/public/*.svg` assets.
//
// Transportation drills into the subcategory with "car" as the catch-all —
// every subcategory the legacy table didn't list (Bus, Taxi, Van,
// DriverServices, Motorcycle, Cycling, Other, none) fell through to the
// car glyph. The legacy `Car.svg` / `Trainfront.svg` assets were baked at
// stroke 1.25; everything else (incl. `Ship.svg`) at stroke 1.
const TRANSPORTATION_MARKER_GLYPHS: Record<string, MarkerGlyph> = {
  Cruise: glyph(Ship),
  Ferry: glyph(Ship),
  Flight: glyph(PlaneTakeoff),
  Helicopter: glyph(PlaneTakeoff),
  Train: glyph(TrainFront, 1.25),
  RentalCar: glyph(Car, 1.25),
};
const TRANSPORTATION_FALLBACK_GLYPH = glyph(Car, 1.25);

// Activity drills into the subcategory; each entry mirrors the legacy
// `/public/{Subcategory}.svg` asset (lucide where the asset was a lucide
// export, the ported legacy component where it wasn't). Subcategories the
// legacy build had no asset for fall back to the Activity walker.
const ACTIVITY_MARKER_GLYPHS: Record<string, MarkerGlyph> = {
  Activity: glyph(PersonWalkingIcon),
  AmusementPark: glyph(RollerCoaster),
  ArtGallery: glyph(Image),
  BoatActivity: glyph(Sailboat),
  CookingCourse: glyph(ChefHat),
  Cultural: glyph(University),
  Event: glyph(CalendarDays),
  FishingHunting: glyph(Fish),
  Gym: glyph(Dumbbell),
  Hiking: glyph(HikingIcon),
  Leisure: glyph(Armchair),
  Museum: glyph(MuseumIcon),
  Other: glyph(BriefcaseBusiness),
  Safari: glyph(JeepIcon),
  Sightseeing: glyph(Landmark),
  Spa: glyph(MassageIcon),
  Sport: glyph(Trophy),
  SwimmingPool: glyph(Waves),
  TeamActivities: glyph(TeamIcon),
  ThemeParty: glyph(PartyPopper),
  Tour: glyph(Binoculars),
  Wine: glyph(Wine),
  Zoo: glyph(LionIcon),
};
const ACTIVITY_FALLBACK_GLYPH = glyph(PersonWalkingIcon);

// All other categories resolve at the category level — mirrors the legacy
// `/public/{Category}.svg` assets.
const CATEGORY_MARKER_GLYPHS: Record<string, MarkerGlyph> = {
  Accommodation: glyph(House),
  Activity: ACTIVITY_FALLBACK_GLYPH,
  Attraction: glyph(Image),
  Destination: glyph(TreePalm),
  Dining: glyph(Utensils),
  Information: glyph(Info),
  Other: glyph(BriefcaseBusiness),
  Personnel: glyph(UserRound),
  Transportation: TRANSPORTATION_FALLBACK_GLYPH,
  Venue: glyph(Landmark),
};
const UNKNOWN_MARKER_GLYPH = glyph(HelpCircle);

// Map-marker glyph resolver — legacy-map parity. Transportation and
// Activity drill into the subcategory; everything else uses the category.
export const getMarkerGlyph = (
  category: string | null,
  subcategory?: string | null
): MarkerGlyph => {
  if (category === 'Transportation') {
    return (
      (subcategory && TRANSPORTATION_MARKER_GLYPHS[subcategory]) ||
      TRANSPORTATION_FALLBACK_GLYPH
    );
  }
  if (category === 'Activity') {
    return (
      (subcategory && ACTIVITY_MARKER_GLYPHS[subcategory]) ||
      ACTIVITY_FALLBACK_GLYPH
    );
  }
  return (category && CATEGORY_MARKER_GLYPHS[category]) || UNKNOWN_MARKER_GLYPH;
};

// Category icons for the products Category filter dropdown — ported 1:1
// from the legacy `lib/data/categories.ts` list (a deliberately different
// set from the map glyphs above; e.g. Activity is the lucide pulse icon
// here, the walking figure on the map). Keyed by the category label the
// faceted filter returns.
const CATEGORY_FILTER_ICONS: Record<string, LucideIcon> = {
  Accommodation: MapPinHouse,
  Activity: Activity,
  Attraction: BookImage,
  Destination: MountainSnow,
  Dining: Utensils,
  Information: Info,
  Other: BriefcaseBusiness,
  Personnel: UserRound,
  Transportation: PlaneTakeoff,
  Venue: Landmark,
};

export const getCategoryFilterIcon = (
  category: string | null
): LucideIcon | null => (category && CATEGORY_FILTER_ICONS[category]) || null;
